import { ArrowRight, Compass, BarChart3, ClipboardCheck } from "lucide-react";
import Reveal from "./Reveal";

function getRefFromCookie() {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|; )vg_ref=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

function withRef(path) {
  const ref = getRefFromCookie();
  return ref ? `${path}${path.includes("?") ? "&" : "?"}ref=${encodeURIComponent(ref)}` : path;
}

const CARDS = [
  {
    id: "orizzonti",
    icon: Compass,
    label: "1 · CHIARISCI LA DIREZIONE",
    name: "OrizzontiPro",
    title: "Racconta il progetto che hai in mente",
    body:
      "Parti dalla tua storia, dai dubbi e dal risultato che vorresti ottenere. Riceverai una prima lettura dello scenario e dei punti che meritano attenzione.",
    cta: "Inizia OrizzontiPro",
    href: "/orizzonti",
  },
  {
    id: "valutapro",
    icon: BarChart3,
    label: "2 · COMPRENDI IL VALORE",
    name: "ValutaPro",
    title: "Scopri il potenziale del tuo immobile",
    body:
      "Analizza caratteristiche, zona e fattori di vendibilità per ottenere una stima indicativa e capire quali elementi incidono maggiormente.",
    cta: "Inizia ValutaPro",
    href: "/valuta",
  },
  {
    id: "partenzapro",
    icon: ClipboardCheck,
    label: "3 · VERIFICA LA PREPARAZIONE",
    name: "PartenzaPro",
    title: "Individua ciò che potrebbe rallentarti",
    body:
      "Controlla documenti, tempi, criticità e priorità prima di compiere il prossimo passo.",
    cta: "Inizia PartenzaPro",
    href: "/partenza",
  },
];

export default function PathSection() {
  return (
    <section
      id="come-funziona"
      data-testid="path-section"
      className="bg-white pt-28 md:pt-36 pb-16 md:pb-20"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <Reveal className="text-center max-w-3xl mx-auto">
          <h2 className="font-display font-bold text-[#0A0A0A] text-4xl md:text-5xl tracking-tight leading-[1.1]">
            Scegli il tuo punto di partenza
          </h2>
          <p className="mt-6 text-[#0A0A0A]/75 text-lg md:text-xl leading-relaxed">
            Una casa può avere un buon valore e restare bloccata per mesi. Può produrre reddito sulla carta e non sostenere il progetto della famiglia. Può essere venduta nel momento sbagliato perché nessuno ha verificato documenti, tempi e alternative.
          </p>
          <p className="mt-5 text-[#0A0A0A]/70 text-base md:text-lg leading-relaxed">
            Ogni strumento risponde a una domanda diversa. Puoi seguire il percorso consigliato oppure utilizzare soltanto ciò che ti serve oggi.
          </p>
        </Reveal>

        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-6">
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            return (
              <Reveal key={card.id} delay={i * 100}>
                <div
                  data-testid={`card-${card.id}`}
                  className="group relative bg-white rounded-xl vg-card-shadow border-t-[4px] border-[#B40101] p-7 md:p-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(0,0,0,0.12)] h-full flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#B40101]/8 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#B40101]" strokeWidth={2} />
                    </div>
                    <span className="text-[11px] font-bold text-[#B40101] tracking-widest uppercase pt-2">
                      {card.label}
                    </span>
                  </div>

                  <h3 className="mt-6 font-display font-bold text-2xl md:text-[26px] text-[#0A0A0A] tracking-tight leading-tight">
                    {card.name}
                  </h3>
                  <p className="mt-2 text-[#0A0A0A] font-semibold text-[15px] md:text-[16px] leading-snug">
                    {card.title}
                  </p>
                  <p className="mt-4 text-[#0A0A0A]/75 leading-relaxed text-[15px] md:text-[16px] flex-1">
                    {card.body}
                  </p>

                  <a
                    data-testid={`cta-${card.id}`}
                    href={withRef(card.href)}
                    className="mt-7 inline-flex items-center justify-center gap-2 w-full bg-[#B40101] hover:bg-[#8E0101] text-white font-semibold px-5 py-3 rounded-xl transition-all duration-200 hover:shadow-[0_10px_28px_rgba(180,1,1,0.3)]"
                  >
                    {card.cta} <ArrowRight className="w-5 h-5" />
                  </a>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={360}>
          <p
            data-testid="path-section-note"
            className="mt-12 md:mt-14 text-center text-[#0A0A0A]/70 text-[15px] md:text-[17px] leading-relaxed max-w-3xl mx-auto"
          >
            Ogni analisi completa arriva nella tua email. Sullo schermo trovi una sintesi concreta e il passaggio successivo consigliato.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
